import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Wallet, TrendingUp, Users, Calendar, Megaphone } from 'lucide-react'
import type { CommissionSummary } from '@/types/commission'

interface CommissionSummaryCardsProps {
  summary?: CommissionSummary
  isLoading?: boolean
}

export function CommissionSummaryCards({ summary, isLoading }: CommissionSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-3 w-20 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const defaultSummary: CommissionSummary = {
    total_amount: 0,
    total_count: 0,
    locker_amount: 0,
    tracker_amount: 0,
    promoter_amount: 0,
    current_month_amount: 0,
  }

  const data = summary || defaultSummary

  const fmt = (v: number | undefined) => (v ?? 0).toFixed(2)

  const cards = [
    {
      title: '佣金总额',
      value: `¥${fmt(data.total_amount)}`,
      description: `${data.total_count ?? 0} 笔记录`,
      icon: Wallet,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: '本月佣金',
      value: `¥${fmt(data.current_month_amount)}`,
      description: '当月累计',
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: '锁客佣金',
      value: `¥${fmt(data.locker_amount)}`,
      description: '锁客类型累计',
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: '跟进佣金',
      value: `¥${fmt(data.tracker_amount)}`,
      description: '跟进类型累计',
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      title: '推广佣金',
      value: `¥${fmt(data.promoter_amount)}`,
      description: '推广类型累计',
      icon: Megaphone,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ]

  return (
    <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <div className={`${card.bgColor} ${card.color} p-2 rounded-md`}>
              <card.icon className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            <p className="text-xs text-muted-foreground mt-1">{card.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
